/**
 * CompanionThread — the family's conversation with their companion, wherever
 * it is mounted (#2266, Victor 2026-10-06: "jumping to a new page is not
 * acceptable").
 *
 * It used to be the body of `/chat`, which the parent home navigated into on
 * every send (#1831 made that page immersive; the 2026-10-05 handoff plan
 * smoothed the jump). The conversation now stays on the home: this component
 * is the thread, its composer and its history, and two hosts mount it:
 *
 *   - `DashboardPage` (`variant="home"`, `lazy`): between the callouts and the
 *     Ideas. An empty thread renders nothing, so a quiet visit keeps the
 *     home's blank-slate shape. Lazy: no stream is opened until the family
 *     sends, picks an idea, follows a noticing, opens the history or starts
 *     a call. Until then the same composer renders, dormant.
 *   - `ChatPage` (`variant="page"`): `/chat/:id` and `/chat?conversationId=`
 *     stay real routes, so a push deep link still opens its thread — now in
 *     the normal app shell (#2266 removed `/chat` from the immersive routes).
 *
 * What it owns, carried over from ChatPage whole: `useChat`, the core list on
 * the prose surface with `renderTrfTurn`, the error row, the translation
 * banner, the handoff pill, the escalation banner, and every composer state
 * the server enforces — a crisis pause (#2566), an ended thread (#1731 phase
 * 6), a case manager on the thread — so no host ever shows a composer the
 * server would refuse. The history is the core `HamburgerDrawer`, anchored
 * RIGHT: a collapsed column beside the thread on desktop, the z-[60] overlay
 * on a phone (§2.10), toggled from the history icon on the composer. It is
 * collapsed on every load; open/closed is session state only.
 *
 * Hosts talk to it through `requests` — a send (router-state prefill, a
 * noticing CTA), a voice start, a fresh thread — each acted on once, by id.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Trash2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useChatCopy } from '@/chat-core/config';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MonoLabel } from '@/components/editorial';
import { AttachReceiptDialog } from '@/components/chat/AttachReceiptDialog';
import { HamburgerDrawer, type ConversationExportFormat } from '@core/chat/components/HamburgerDrawer';
import { SessionEndedBanner } from '@core/chat/components/SessionEndedBanner';
import { useCopyConversation } from '@core/chat/hooks/useCopyConversation';
import { openPrintExport } from '@core/chat/services/printExportService';
import { formatConversationAsMarkdown, formatConversationAsText, buildExportFilename, triggerBlobDownload } from '@core/chat/utils/conversation-formatters';
import { filterByPreset, type SlicePreset } from '@core/chat/utils/time';
import { EMPTY_SELECTION, countSelected, nextSelection, sliceBySelection, type MessageRangeSelection } from '@core/chat/utils/message-selection';
import { MessageList as CoreMessageList } from '@core/chat/components/MessageList';
import { ChatSurfaceProvider } from '@core/config';
import { useChatConfig } from '@chat-config';
import { useMediaQuery } from '@/features/chat-trf/useMediaQuery';
import { toCoreMessages } from '@/features/chat-trf/adapter/messages';
import { renderTrfTurn } from '@/features/chat-trf/turns/renderTrfTurn';
import { useChatVoicePip } from '@/features/chat-trf/voice/useChatVoicePip';
import { getPipVoiceSession } from '@/features/chat-trf/voice/pipVoiceStore';
import { ChatErrorRow } from '@/features/chat-trf/ChatErrorRow';
import { ActionConfirmCard } from '@/features/chat-trf/receipts/ActionConfirmCard';
import { companionSpeaksUiLanguage, isEnglishUi, useUiLanguage } from '@/features/chat-trf/language';
import { ThreadTranslationBanner } from '@/components/handoff/ThreadTranslationBanner';
import { hasTranslationMetadata } from '@/lib/translation-utils';
import { EscalationBanner } from '@/features/chat-trf/crisis/EscalationBanner';
import { CrisisDisclosure } from '@/components/legal/CrisisDisclosure';
import { HandoffStatusPill } from '@/features/chat-trf/handoff/HandoffStatusPill';
import { LoopTrfButton } from '@/features/chat-trf/handoff/LoopTrfButton';
import { HandoffNudgeCard, type NudgeCategory } from '@/features/chat-trf/handoff/HandoffNudgeCard';
import { useChat } from '@/hooks/useChat';
import { useConversationModeSync } from '@/hooks/useConversationModeSync';
import { FAMILY_PENDING_QUERY_KEY } from '@/hooks/useHandoffFamilyEntry';
import { useStagedMessages } from '@/hooks/useStagedMessages';
import { usePersonaPreferences } from '@/hooks/usePersonaPreferences';
import { VoiceCharacterSelector } from '@/components/voice/VoiceCharacterSelector';
import { chatApi } from '@/services/chatApi';
import { api } from '@/services/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalFooter, ModalHeader } from '@/components/admin/editorial-modal';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { cn, personaFirstName } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';
import { DELETE_CONV_SKIP_CONFIRM_KEY } from '@/lib/user-scoped-storage';
import { invalidateConversationLists } from '@/components/companion/useConversations';
import { ComposerHero, type ComposerHeroIdeasState } from '@/components/companion/ComposerHero';
import type { ChatMessageState } from '@/types/chat';
import { CONNECT_STALL_MS, companionRequest, type CompanionRequest } from './companionRequests';

/** What the host's Ideas list is told: the composer state, and how to send. */
export interface CompanionIdeasState extends ComposerHeroIdeasState {
  send: (text: string) => void;
}

export interface CompanionThreadProps {
  /** `home`: the parent home. `page`: the `/chat/:id` route. */
  variant: 'home' | 'page';
  /** The thread to open. Omitted → a fresh one (CP-16: every open is a new thread). */
  initialConversationId?: string;
  /** The thread in view changed (a history pick, a new thread, a call's thread). */
  onConversationChange?: (id: string) => void;
  /** Sends, voice starts, fresh threads — each acted on once, by id. */
  requests?: CompanionRequest[];
  /**
   * The thread has taken request `id`: the host drops it from `requests`.
   * The queue lives in the host, so a request the host still holds after a
   * remount of this component would be acted on again.
   */
  onRequestHandled?: (id: number) => void;
  /** Home: open no stream until the family first uses the companion. */
  lazy?: boolean;
  /** The composer section's accessible name. */
  ariaLabel: string;
  /** The composer placeholder ("Ask Maya anything…"). */
  placeholder: string;
  /** Ideas for you under the composer — the host's list, shown at the start only. */
  renderIdeas?: (state: CompanionIdeasState) => ReactNode;
  /** What an empty thread shows; nothing by default (the home's blank slate). */
  renderEmpty?: (send: (text: string) => void) => ReactNode;
  /** Bounds the thread's own scrollport (`max-h-*`), so the page around it stays put. */
  threadClassName?: string;
}

// ── The history door ──────────────────────────────────────────────────────

interface HistoryToggleProps {
  open: boolean;
  onToggle: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
}

/** The history icon on the composer (2026-09-21); since #2266 it toggles the drawer. */
function HistoryToggle({ open, onToggle, buttonRef }: HistoryToggleProps) {
  const { t } = useTranslation('companion');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={buttonRef}
          type="button"
          variant="ghost"
          size="sm"
          // #2643 (a), D-2420: 32 px drawn, 44 px reach on a coarse pointer
          // (6 px past each edge). It stands alone: the composer sits 8 px
          // below, beyond the reach.
          className={cn(
            'h-8 w-8 p-0 text-stevens-ink-2 hover:text-foreground',
            COARSE_HIT_ALONE,
            '[@media(pointer:coarse)]:after:-inset-1.5',
          )}
          aria-label={t('history.title')}
          aria-expanded={open}
          onClick={onToggle}
        >
          <History className="h-4 w-4" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{t('history.title')}</TooltipContent>
    </Tooltip>
  );
}

function ChatSkeleton() {
  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      <Skeleton className="h-16 w-16 rounded-full" />
      <div className="space-y-2 text-center">
        <Skeleton className="h-8 w-48 mx-auto" />
        <Skeleton className="h-4 w-72 mx-auto" />
      </div>
    </div>
  );
}

// ── The dormant composer (home, before first use) ─────────────────────────

type WakeReason =
  | { kind: 'mount' }
  | { kind: 'compose' }
  | { kind: 'history' }
  | { kind: 'receipt'; file: File };

/**
 * The thread wrapper. On the home it starts DORMANT: the same composer, the
 * same history icon and the same Ideas render, but no `useChat`, so a visit
 * that only reads the page opens no stream. The first use wakes the engine
 * and hands it what woke it.
 */
export function CompanionThread(props: CompanionThreadProps) {
  const { lazy = false, requests, onRequestHandled } = props;
  const [wake, setWake] = useState<WakeReason | null>(lazy ? null : { kind: 'mount' });
  const [localRequests, setLocalRequests] = useState<CompanionRequest[]>([]);
  // What the parent has typed into the dormant composer. Waking swaps it for
  // the engine's composer, which starts from this draft — opening the
  // history or the clip must not wipe a half-written message. A send wakes
  // with an empty box: the draft was the message.
  const [dormantDraft, setDormantDraft] = useState('');
  const [wakeDraft, setWakeDraft] = useState('');
  const hostAsked = (requests?.length ?? 0) > 0;
  // A host request wakes the engine for good: once the request is handled
  // the host drops it, and the thread it started must stay mounted.
  if (!wake && hostAsked) {
    setWake({ kind: 'compose' });
    setWakeDraft(dormantDraft);
  }
  const allRequests = useMemo(
    () => [...(requests ?? []), ...localRequests],
    [requests, localRequests],
  );
  // Each handled request leaves the queue that holds it — this wrapper's own
  // (the dormant composer's first send) or the host's.
  const onRequestHandledRef = useRef(onRequestHandled);
  onRequestHandledRef.current = onRequestHandled;
  const handleRequestHandled = useCallback((id: number) => {
    setLocalRequests((current) => (current.some((r) => r.id === id) ? current.filter((r) => r.id !== id) : current));
    onRequestHandledRef.current?.(id);
  }, []);

  if (!wake && !hostAsked) {
    return (
      <DormantComposer
        {...props}
        onDraftChange={setDormantDraft}
        onWake={(reason, request) => {
          if (request) setLocalRequests((r) => [...r, request]);
          setWakeDraft(request?.kind === 'send' ? '' : dormantDraft);
          setWake(reason);
        }}
      />
    );
  }
  return (
    <ThreadEngine
      {...props}
      requests={allRequests}
      onRequestHandled={handleRequestHandled}
      wake={wake ?? { kind: 'compose' }}
      wakeDraft={wakeDraft}
    />
  );
}

interface DormantComposerProps extends CompanionThreadProps {
  onWake: (reason: WakeReason, request?: CompanionRequest) => void;
  onDraftChange: (text: string) => void;
}

function DormantComposer({ ariaLabel, placeholder, renderIdeas, onWake, onDraftChange }: DormantComposerProps) {
  const send = (text: string) => onWake({ kind: 'compose' }, companionRequest('send', text));
  return (
    <div data-testid="chat-panel" className="min-w-0">
      <div data-testid="chat-composer">
        <ComposerHero
          ariaLabel={ariaLabel}
          placeholder={placeholder}
          onSubmit={send}
          onDraftChange={onDraftChange}
          onVoice={() => onWake({ kind: 'compose' }, companionRequest('voice'))}
          trailing={<HistoryToggle open={false} onToggle={() => onWake({ kind: 'history' })} />}
          inputProps={{
            attachments: 'receipt',
            onFileSelect: (file) => onWake({ kind: 'receipt', file }),
          }}
          renderIdeas={renderIdeas ? (state) => renderIdeas({ ...state, send }) : undefined}
        />
        <div className="text-center pt-1">
          <CrisisDisclosure variant="inline-link" />
        </div>
      </div>
    </div>
  );
}

// ── The engine ────────────────────────────────────────────────────────────

interface ThreadEngineProps extends CompanionThreadProps {
  wake: WakeReason;
  /** The dormant composer's draft at wake: the engine's composer starts from it. */
  wakeDraft?: string;
}

function ThreadEngine({
  variant,
  initialConversationId,
  onConversationChange,
  requests,
  onRequestHandled,
  ariaLabel,
  placeholder,
  renderIdeas,
  renderEmpty,
  threadClassName,
  wake,
  wakeDraft = '',
}: ThreadEngineProps) {
  const { user, translation } = useAuth();
  const { t } = useTranslation(['chat', 'common', 'companion']);
  const uiLanguage = useUiLanguage();
  // #2109 R3-B11: a server or engine sentence is English; only an English
  // reader is shown one. Everyone else reads the localized line instead.
  const englishUi = isEnglishUi(uiLanguage);
  // #1803 (3): the reading rail is the ONE scrollport for the thread. The core
  // list takes it as its `scrollContainerRef`, which is also what portals the
  // jump-to-latest pill into it — `position: sticky` only ever offsets inside
  // the element that actually scrolls.
  const chatRailRef = useRef<HTMLDivElement>(null);
  const [initialId] = useState(() => initialConversationId ?? crypto.randomUUID());
  const onConversationChangeRef = useRef(onConversationChange);
  onConversationChangeRef.current = onConversationChange;

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // #2266: the history is a right drawer, collapsed on every load (desktop and
  // phone alike) and opened from the composer's history icon. Session state:
  // nothing is stored.
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [historyOpen, setHistoryOpen] = useState(() => wake.kind === 'history');
  // #2617: when the drawer closes (Escape, the scrim, Hide) the control that
  // had focus goes with it — the drawer turns `inert` and the browser drops
  // focus to <body>. Hand it to the history icon that reopens it, the same
  // control that opened it. A close that moved focus somewhere real (the
  // delete confirm opening) is left alone.
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const historyWasOpenRef = useRef(historyOpen);
  useEffect(() => {
    const wasOpen = historyWasOpenRef.current;
    historyWasOpenRef.current = historyOpen;
    if (!wasOpen || historyOpen) return;
    const active = document.activeElement;
    if (!active || active === document.body || active.closest('aside[inert]')) {
      historyButtonRef.current?.focus();
    }
  }, [historyOpen]);
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(
    () => localStorage.getItem(DELETE_CONV_SKIP_CONFIRM_KEY) === 'true'
  );
  const queryClient = useQueryClient();

  const {
    messages,
    status,
    isTyping,
    isStreaming,
    error,
    conversationId,
    escalation,
    pendingAction,
    isConfirming,
    mode,
    setMode,
    sendMessage,
    retryLastMessage,
    switchConversation,
    pendingReceipt,
    attachReceipt,
    clearReceipt,
    startNewConversation,
    dismissEscalation,
    dismissError,
    confirmAction,
    cancelAction,
    reconnect,
    recall,
    reanchorKey,
    sessionEnded,
    markSessionEnded,
    markSessionResumed,
    isVoiceThread,
    reloadMessages,
  } = useChat(initialId);

  // #1731 phase 6: the resume call's in-flight and error state. The banner
  // disables both actions while `resuming` and keeps the composer hidden when
  // `resumeError` is set — a failed resume must not look like a live thread.
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);

  // B-597: a bounded wait on the stream. After CONNECT_STALL_MS in
  // `connecting`, or at once on `error` / `disconnected`, the skeleton yields
  // to a callout with Retry.
  const [connectStalled, setConnectStalled] = useState(false);
  useEffect(() => {
    if (status !== 'connecting') {
      setConnectStalled(false);
      return;
    }
    const timer = setTimeout(() => setConnectStalled(true), CONNECT_STALL_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const { displayMessages: rawDisplayMessages, isDisplayTyping } = useStagedMessages(
    messages,
    isTyping,
    isStreaming,
    conversationId
  );

  // Local-only dismissals for the SLA-breach surface — for the current
  // viewing session and that handoff only; a different thread starts fresh.
  const [dismissedSlaHandoffIds, setDismissedSlaHandoffIds] = useState<Set<string>>(() => new Set());
  const [dismissedSystemMessageIds, setDismissedSystemMessageIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setDismissedSlaHandoffIds(new Set());
    setDismissedSystemMessageIds(new Set());
  }, [conversationId]);

  // A message a host asked to send (`requests`) waits here until the stream
  // is up, and shows as a `sending` bubble meanwhile — it must never exist
  // nowhere on screen (2026-10-05). A stream that fails drops the bubble, so
  // the connect-error callout can show. Several can wait at once (a prefill
  // and a CTA before the stream is up); they go in order, one per turn.
  const [sendQueue, setSendQueue] = useState<{ id: number; text: string }[]>([]);
  const queued = sendQueue.length > 0 ? sendQueue[0].text : null;
  const pendingBubble = useMemo<ChatMessageState | null>(
    () => (queued === null
      ? null
      : { id: 'pending-prefill', role: 'user', content: queued, timestamp: new Date().toISOString(), status: 'sending' }),
    [queued],
  );
  const showPendingBubble =
    pendingBubble !== null && (status === 'connected' || (status === 'connecting' && !connectStalled));
  const displayMessages = useMemo(() => {
    const kept = dismissedSystemMessageIds.size === 0
      ? rawDisplayMessages
      : rawDisplayMessages.filter((m) => !dismissedSystemMessageIds.has(m.id));
    return showPendingBubble && pendingBubble ? [...kept, pendingBubble] : kept;
  }, [rawDisplayMessages, dismissedSystemMessageIds, showPendingBubble, pendingBubble]);

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => chatApi.getConversations(),
    refetchInterval: 30000,
  });

  // Pending implicit-handoff nudges for this family (silence, lifecycle,
  // claim_stress) — `HandoffNudgeCard` lets the family opt in or dismiss.
  const { data: familyPendingResp, refetch: refetchFamilyPending } = useQuery({
    queryKey: FAMILY_PENDING_QUERY_KEY,
    queryFn: () => api.handoffs.familyPending(),
    refetchInterval: 30_000,
  });
  // #1801: the family-facing loop-in is dark until TRF confirms that a case
  // manager really will join a thread live. The flag rides on this same
  // response (`family_entry_enabled`). Absent → off.
  const handoffFamilyEntryEnabled = familyPendingResp?.family_entry_enabled === true;
  const pendingNudge = !handoffFamilyEntryEnabled ? undefined : familyPendingResp?.data?.find((r) => {
    if (r.status !== 'awaiting_family') return false;
    return r.origin === 'implicit_silence'
        || r.origin === 'implicit_lifecycle'
        || r.origin === 'implicit_claim';
  });
  const nudgeCategory: NudgeCategory | null = pendingNudge
    ? (pendingNudge.origin === 'implicit_silence' ? 'silence'
       : pendingNudge.origin === 'implicit_claim' ? 'claim_stress'
       : 'lifecycle')
    : null;

  // The active conversation's handoff state, from the conversations list.
  const currentConversation = conversations.find((c) => c.id === conversationId);
  const conversationMode = currentConversation?.mode;
  const expectedResponseAt = currentConversation?.expected_response_at ?? null;

  // Sync the BACKEND-reported mode into useChat only when it changes (see
  // useConversationModeSync for why local SSE-driven changes are not reverted).
  const chatCopy = useChatCopy();
  useConversationModeSync({ conversationMode, mode, setMode });

  // #2566: a critical Guardian flag pauses the conversation. The banner stays
  // for as long as the conversation is paused — after a reload too — and goes
  // when the family continues.
  const crisisPaused = mode === 'crisis_paused';
  const [crisisResuming, setCrisisResuming] = useState(false);
  const [crisisResumeFailed, setCrisisResumeFailed] = useState(false);
  const handleResumeAfterCrisis = useCallback(async () => {
    if (!conversationId) return;
    setCrisisResuming(true);
    setCrisisResumeFailed(false);
    try {
      await chatApi.resumeAfterCrisis(conversationId);
      setMode('ai');
      dismissEscalation();
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      setCrisisResumeFailed(true);
    } finally {
      setCrisisResuming(false);
    }
  }, [conversationId, setMode, dismissEscalation, queryClient]);
  const bannerEscalation = crisisPaused
    ? { priority: '', timestamp: 0, ...(escalation ?? {}), level: 'critical' as const }
    : escalation;

  const handleSwitchConversation = useCallback(
    (id: string) => {
      onConversationChangeRef.current?.(id);
      switchConversation(id);
    },
    [switchConversation]
  );

  // #1731 phase 6 (CP-16, DECIDED Victor 2026-09-05): an unknown id is the
  // fresh thread THIS open minted, and it is kept. The previous conversation
  // is one tap away in the history and is never restored under the family's
  // feet, so there is no auto-switch to "most recent".
  const handleNewConversation = useCallback((): string => {
    const newId = startNewConversation();
    onConversationChangeRef.current?.(newId);
    // "New conversation" lands on a fresh text thread; a live call keeps
    // floating over it (the PiP is app-level, #1731).
    return newId;
  }, [startNewConversation]);

  // #1731 phase 6: the backend is the source of truth for status. These call
  // the route FIRST and only then sync local state.
  const handleResumeConversation = useCallback(async () => {
    setResuming(true);
    setResumeError(null);
    try {
      await chatApi.resumeConversation(conversationId);
      markSessionResumed();
    } catch {
      setResumeError(t('page.resumeFailed'));
    } finally {
      setResuming(false);
    }
  }, [conversationId, markSessionResumed, t]);

  const handleEndConversation = useCallback(async () => {
    try {
      await chatApi.endConversation(conversationId);
    } catch {
      // Ending is idempotent server-side; locking the surface is the honest
      // outcome either way.
    }
    markSessionEnded();
  }, [conversationId, markSessionEnded]);

  // #2389: the first message on a thread is what creates it on the server.
  // Once that send settles, mark every conversations list stale, so the
  // history drawer shows the new thread without a poll; the lists refresh once
  // more when the first reply (which titles the thread) has arrived.
  const knownThreadsRef = useRef(conversations);
  knownThreadsRef.current = conversations;
  const awaitingFirstReplyRef = useRef<string | null>(null);

  const handleSendMessage = useCallback(
    (content: string) => {
      const opensThread = !knownThreadsRef.current.some((c) => c.id === conversationId);
      if (opensThread) awaitingFirstReplyRef.current = conversationId;
      void Promise.resolve(sendMessage(content)).then(() => {
        if (opensThread) void invalidateConversationLists(queryClient);
      });
    },
    [sendMessage, conversationId, queryClient]
  );

  const firstReplyLanded =
    !isStreaming && !isTyping && messages.some((m) => m.role === 'assistant' && m.status !== 'error');
  useEffect(() => {
    if (awaitingFirstReplyRef.current === null) return;
    if (awaitingFirstReplyRef.current !== conversationId) {
      awaitingFirstReplyRef.current = null;
      return;
    }
    if (!firstReplyLanded) return;
    awaitingFirstReplyRef.current = null;
    void invalidateConversationLists(queryClient);
  }, [firstReplyLanded, conversationId, queryClient]);

  // #641: the paperclip's file pick opens the attach-receipt mini-form. The
  // dialog is open while a picked file is staged here — a pick on the dormant
  // home composer arrives as the wake reason.
  const [receiptFile, setReceiptFile] = useState<File | null>(
    () => (wake.kind === 'receipt' ? wake.file : null),
  );

  const { toast } = useToast();

  const handleLoopTrfCreated = useCallback(() => {
    // Optimistic mode flip — the server is creating the handoff request.
    setMode('human_handoff');
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [setMode, queryClient]);

  const handleLoopTrfError = useCallback(
    (message: string) => {
      toast({
        variant: 'destructive',
        title: t('page.toasts.loopInFailed'),
        description: englishUi ? message : t('common:states.pleaseTryAgain'),
      });
    },
    [toast, t, englishUi],
  );

  // #1731 (F-10, F-15): the voice call is the app-level PiP session
  // (GlobalVoicePip in MainLayout). The thread merges its live turns and
  // re-reads itself when a call on it ends.
  const handleCallEnded = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    await reloadMessages();
  }, [queryClient, reloadMessages]);
  const voicePip = useChatVoicePip(conversationId, handleCallEnded);

  const { preferences: personaPrefs, isLoading: personaPrefsLoading } = usePersonaPreferences();
  const hasChosen = !!personaPrefs?.persona;
  // While the persona query is in flight the speaker name would resolve to
  // the platform default; `personaPrefsLoading` holds the thread (#1328).
  const personaName = personaFirstName(personaPrefs?.personaConfig?.personaName);
  // #1731: the audience config the core reads — companion, named per family.
  // #2109 R3-B24: `useChatConfig` re-resolves on a language switch.
  const surfaceConfig = useChatConfig('companion', { agentName: personaName });
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorMode, setSelectorMode] = useState<'first-time' | 'settings'>('first-time');

  // A call started from the blank slate (or from a thread the backend has not
  // seen) mints a thread: adopt it, so the spoken turns land where the family
  // is looking (spec §4.3). A call viewed from another thread must not drag
  // the thread back.
  const pipConversationId = voicePip.pip.conversationId;
  const pipThreadId = voicePip.pip.threadId;
  useEffect(() => {
    if (!pipConversationId || pipConversationId === conversationId) return;
    if (pipThreadId !== null && pipThreadId !== conversationId) return;
    handleSwitchConversation(pipConversationId);
  }, [pipConversationId, pipThreadId, conversationId, handleSwitchConversation]);

  const startVoice = voicePip.startVoice;
  const startVoiceOrPrompt = useCallback(() => {
    if (!hasChosen) {
      setSelectorMode('first-time');
      setSelectorOpen(true);
      return;
    }
    startVoice({ sourceConversationId: conversationId ?? null });
  }, [hasChosen, conversationId, startVoice]);

  const endVoice = useCallback(() => {
    void getPipVoiceSession().end();
  }, []);

  // ── Duplex (#1731 phase 5, F-7 / C-27) ─────────────────────────────────
  // While a call is live a typed turn belongs to it. The voice path owns the
  // message even when refused: the composer pauses with the audience line and
  // the draft goes back through `initialValue`. Ideas and requests are typed
  // turns too. It also carries the dormant composer's draft across the wake.
  const [restoredDraft, setRestoredDraft] = useState(wakeDraft);
  const { isLive: voiceIsLive, trySendText } = voicePip;
  // #2265: the threads that have had a send in this session. Ideas show only
  // at the start of a conversation and never come back for a thread once the
  // family has sent on it — even if the turn then failed or was cleared.
  const [startedThreads, setStartedThreads] = useState<ReadonlySet<string>>(() => new Set());
  const handleComposerSend = useCallback(
    (text: string) => {
      setStartedThreads((started) => (started.has(conversationId) ? started : new Set(started).add(conversationId)));
      setRestoredDraft('');
      if (!voiceIsLive) {
        handleSendMessage(text);
        return;
      }
      void trySendText(text).then((handled) => {
        if (!handled) {
          handleSendMessage(text);
          return;
        }
        if (handled === 'refused') setRestoredDraft(text);
      });
    },
    [voiceIsLive, trySendText, handleSendMessage, conversationId],
  );
  const composerBusy = isStreaming || isDisplayTyping;
  const composerDisabled = status !== 'connected' || composerBusy || voicePip.duplexPaused;
  const duplexNotice = voicePip.duplexNotice && voicePip.isLive ? (
    <p role="status" className="mb-2 text-caption text-muted-foreground">
      {voicePip.duplexNotice}
    </p>
  ) : null;

  // ── Requests from the host, each once ──────────────────────────────────
  // The ref guards this engine between taking a request and the host's
  // queue update landing; `onRequestHandled` is what makes it once for good:
  // the host drops the request, so a remount of the thread cannot take it
  // again (L1r review: the home's error block used to unmount the thread and
  // every queued CTA re-sent on its return).
  const handledRequestsRef = useRef(new Set<number>());
  useEffect(() => {
    for (const request of requests ?? []) {
      if (handledRequestsRef.current.has(request.id)) continue;
      if (request.kind === 'voice') {
        if (personaPrefs === undefined) continue; // wait for prefs to load
        handledRequestsRef.current.add(request.id);
        startVoiceOrPrompt();
      } else if (request.kind === 'new') {
        handledRequestsRef.current.add(request.id);
        handleNewConversation();
      } else {
        // A send is acknowledged when it actually goes (below), so a remount
        // before the stream is up queues it again instead of losing it.
        handledRequestsRef.current.add(request.id);
        const { id, text } = request;
        setSendQueue((current) => [...current, { id, text }]);
        continue;
      }
      onRequestHandled?.(request.id);
    }
  }, [requests, personaPrefs, startVoiceOrPrompt, handleNewConversation, onRequestHandled]);
  // The queued message goes once the stream is up and the composer is free.
  // A host request never goes into a thread the server would refuse or
  // reroute — paused after a crisis flag, ended, or with a TRFer on it (the
  // states where the composer below is hidden or re-themed). It starts a new
  // conversation and goes there, as the old `/chat?new=true` hop did. What the
  // family types in the composer is not a host request and is unchanged.
  //
  // "The stream is up" is per conversation. Switching threads resets the
  // mode, the ended flag and the conversation id at once, but `status` is not
  // reset: `useSSEConnection.disconnect()` closes the old EventSource without
  // reporting a status, and only the new thread's `connect()` reports
  // 'connecting', a commit or more later. So right after a switch `status`
  // still reads the OLD stream's 'connected', and a send then reaches the new
  // id before its stream is open (the server answers 400; L1r3 review). The
  // ref below counts a thread's stream as up only once that conversation has
  // been seen in a state other than 'connected' and is back to 'connected'.
  // The stream a thread mounts with is its own, so 'connected' at mount counts.
  const streamRef = useRef({ conversationId, opening: false, up: status === 'connected' });
  useEffect(() => {
    const stream = streamRef.current;
    if (stream.conversationId !== conversationId) {
      streamRef.current = { conversationId, opening: status !== 'connected', up: false };
      return;
    }
    if (status !== 'connected') {
      stream.opening = true;
      stream.up = false;
    } else if (stream.opening) {
      stream.opening = false;
      stream.up = true;
    }
  }, [conversationId, status]);

  const nextQueued = sendQueue.length > 0 ? sendQueue[0] : null;
  const threadRefusesHostSend = crisisPaused || sessionEnded || mode === 'human_handoff';
  // The request a refusing thread handed to a fresh conversation, and that
  // conversation's id (what `startNewConversation` returned). One new
  // conversation per request, however often the effect runs before the fresh
  // thread's state lands; the request goes only into that id.
  const freshThreadRef = useRef<{ requestId: number; conversationId: string; landed: boolean } | null>(null);
  useEffect(() => {
    if (nextQueued === null) return;
    const fresh = freshThreadRef.current?.requestId === nextQueued.id ? freshThreadRef.current : null;
    if (fresh) {
      if (conversationId !== fresh.conversationId) {
        // Not landed yet: wait for the fresh id. Landed and then left (the
        // family picked another thread first): the request follows them and
        // is judged against the thread they are on now.
        if (!fresh.landed) return;
        freshThreadRef.current = null;
      } else {
        fresh.landed = true;
      }
    }
    const boundToFresh = freshThreadRef.current !== null && freshThreadRef.current.requestId === nextQueued.id;
    if (!boundToFresh && threadRefusesHostSend) {
      freshThreadRef.current = { requestId: nextQueued.id, conversationId: handleNewConversation(), landed: false };
      return;
    }
    const streamUp = streamRef.current.conversationId === conversationId && streamRef.current.up && status === 'connected';
    if (!streamUp || composerBusy) return;
    if (boundToFresh) freshThreadRef.current = null;
    setSendQueue((current) => current.filter((q) => q.id !== nextQueued.id));
    handleComposerSend(nextQueued.text);
    onRequestHandled?.(nextQueued.id);
  }, [nextQueued, threadRefusesHostSend, conversationId, status, composerBusy, handleComposerSend, handleNewConversation, onRequestHandled]);

  const handleDeleteClick = useCallback(
    (convId: string) => {
      if (skipDeleteConfirm) {
        setDeletingId(convId);
        chatApi
          .deleteConversation(convId)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            if (convId === conversationId) {
              handleNewConversation();
            }
          })
          .catch(() => {
            toast({ variant: 'destructive', title: t('page.toasts.deleteFailed'), description: t('common:states.pleaseTryAgain') });
          })
          .finally(() => setDeletingId(null));
      } else {
        setDeletingId(convId);
        setDeleteConfirmOpen(true);
      }
    },
    [skipDeleteConfirm, queryClient, toast, handleNewConversation, conversationId, t]
  );

  const confirmDelete = useCallback(async () => {
    if (!deletingId) return;
    const idToDelete = deletingId;
    setDeletingId(null);
    setDeleteConfirmOpen(false);
    try {
      await chatApi.deleteConversation(idToDelete);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (idToDelete === conversationId) {
        handleNewConversation();
      }
    } catch {
      toast({ variant: 'destructive', title: t('page.toasts.deleteFailed'), description: t('common:states.pleaseTryAgain') });
    }
  }, [deletingId, queryClient, toast, handleNewConversation, conversationId, t]);

  // ─── #1731 (F-9): drawer export, copy and the custom message range ───
  const coreMessagesForExport = useMemo(() => toCoreMessages(messages), [messages]);

  // #1902: the recall line belongs under the reply it explains — attach it to
  // the last agent row that renders, so exactly one line appears.
  const messagesWithRecall = useMemo(() => {
    const rows = toCoreMessages(displayMessages);
    if (!recall) return rows;
    let lastAgent = -1;
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i].sender !== 'user') { lastAgent = i; break; }
    }
    if (lastAgent === -1) return rows;
    const next = rows.slice();
    next[lastAgent] = { ...next[lastAgent], memoryRecall: recall };
    return next;
  }, [displayMessages, recall]);
  const { copyConversation, copied, copying } = useCopyConversation();
  const [rangeSelection, setRangeSelection] = useState<MessageRangeSelection>(EMPTY_SELECTION);
  const [activeSlicePreset, setActiveSlicePreset] = useState<SlicePreset>('everything');
  const selectionMode = activeSlicePreset === 'custom';
  useEffect(() => {
    setRangeSelection(EMPTY_SELECTION);
  }, [conversationId]);
  const handleSelectMessage = useCallback((messageId: string, index: number) => {
    setRangeSelection((current) => nextSelection(current, { id: messageId, index }));
  }, []);
  const clearRangeSelection = useCallback(() => setRangeSelection(EMPTY_SELECTION), []);
  const selectedCount = useMemo(
    () => countSelected(coreMessagesForExport, rangeSelection),
    [coreMessagesForExport, rangeSelection],
  );
  const messageTimestamps = useMemo(
    () => coreMessagesForExport.map((m) => m.timestamp.getTime()),
    [coreMessagesForExport],
  );
  const sliceForExport = useCallback(
    (preset: SlicePreset) =>
      preset === 'custom'
        ? sliceBySelection(coreMessagesForExport, rangeSelection)
        : filterByPreset(coreMessagesForExport, preset),
    [coreMessagesForExport, rangeSelection],
  );
  const handleCopy = useCallback(
    (preset: SlicePreset) => {
      const sliced = sliceForExport(preset);
      if (sliced.length === 0) return;
      void copyConversation(sliced, conversationId);
    },
    [sliceForExport, copyConversation, conversationId],
  );
  const handleDownload = useCallback(
    (format: ConversationExportFormat, preset: SlicePreset) => {
      // No `docx`/`pdf` branch: `serverExportFormats` is empty on this
      // surface (#1731 phase 7).
      const sliced = sliceForExport(preset);
      if (sliced.length === 0) return;
      if (format === 'print') {
        void openPrintExport(sliced, conversationId).catch((err: unknown) => {
          toast({
            variant: 'destructive',
            title: t('page.toasts.printFailed'),
            description: englishUi && err instanceof Error ? err.message : t('page.toasts.printFailedRetry'),
          });
        });
        return;
      }
      const body = format === 'md' ? formatConversationAsMarkdown(sliced, conversationId) : formatConversationAsText(sliced, conversationId);
      triggerBlobDownload(
        new Blob([body], { type: format === 'md' ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8' }),
        buildExportFilename(conversationId, format),
      );
    },
    [sliceForExport, conversationId, toast, t, englishUi],
  );

  // ── The thread ─────────────────────────────────────────────────────────

  const threadEmpty = displayMessages.length === 0 && voicePip.voiceMessages.length === 0;
  const railClass = cn(
    // #2504: the prose rows carry no inset of their own, so the rail carries
    // 16 px a side. #2267: the thread sits on the page ground in the page's
    // own column (the home's `max-w-4xl`), its left edge on the composer's —
    // no centred 768 px card, no chrome of its own.
    'min-h-0 flex flex-col overflow-y-auto overscroll-contain w-full px-4',
    // #2266: bounded, with its own scroll, so the greeting stays on screen
    // and the jump pill and ThinkingIndicator keep ONE scrollport (#1803).
    threadClassName,
  );

  function renderThread() {
    const streamDown = status === 'error' || status === 'disconnected' || (status === 'connecting' && connectStalled);
    // Hold the skeleton until the persona name resolves (#1328) — every
    // speaker label in here is derived from it. The home's blank slate shows
    // nothing instead: an empty thread there is the page's quiet state.
    if (personaPrefsLoading || (status === 'connecting' && !connectStalled && displayMessages.length === 0)) {
      return variant === 'page' ? <div className={railClass}><ChatSkeleton /></div> : null;
    }
    if (streamDown && displayMessages.length === 0) {
      // Nothing to read AND no stream to read from: say so, with a way back,
      // instead of a composer that will not send (B-597).
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center gap-3" role="alert" data-testid="chat-connect-error">
          <p className="text-body font-medium text-foreground">
            {t('page.connectFailed.title', { companion: personaName })}
          </p>
          <p className="text-body-sm text-stevens-ink-3 max-w-sm">
            {(englishUi && error) || t('page.connectFailed.body')}
          </p>
          <Button size="sm" variant="outline" onClick={reconnect}>
            {t('page.retry')}
          </Button>
        </div>
      );
    }
    if (threadEmpty) {
      return renderEmpty ? renderEmpty(handleComposerSend) : null;
    }
    // The active handoff id, from the most recent lifecycle system message.
    const handoffSystemMessage = mode === 'human_handoff'
      ? [...displayMessages].reverse().find(
          (m) => m.role === 'system' && typeof m.metadata?.handoff_request_id === 'string',
        )
      : undefined;
    const activeHandoffId = handoffSystemMessage?.metadata?.handoff_request_id as string | undefined;

    const familyViewerLanguage = user?.voicePreferences?.language || 'en';
    const trferJoinedMsg = [...displayMessages].reverse().find(
      (m) => m.role === 'system' && (m.metadata as { event?: string } | undefined)?.event === 'trfer_joined',
    );
    const otherPartyLanguage =
      (trferJoinedMsg?.metadata as { trfer_preferred_language?: string } | undefined)
        ?.trfer_preferred_language || 'en';
    const trferName =
      (trferJoinedMsg?.metadata as { trfer_name?: string } | undefined)?.trfer_name;
    const otherPartyName = trferName || t('page.yourCaseManager');

    // The live handoff status for the pill, from lifecycle system messages:
    // trfer_joined → claimed; handoff_sla_breached → sla_breached; else
    // waiting. A late claim resolves the breach.
    const slaBreachedMsg = [...displayMessages].reverse().find(
      (m) => m.role === 'system' && (m.metadata as { event?: string } | undefined)?.event === 'handoff_sla_breached',
    );
    const breachDismissed = !!slaBreachedMsg && dismissedSlaHandoffIds.has(activeHandoffId ?? '');
    const derivedHandoffStatus: import('@/types/api').HandoffStatus | undefined = trferJoinedMsg
      ? 'claimed'
      : slaBreachedMsg && !breachDismissed
      ? 'sla_breached'
      : undefined;
    // A dismissed breach with nobody joined suppresses the pill rather than
    // promising an ETA the SLA already missed.
    const suppressPill = breachDismissed && !trferJoinedMsg;

    const dismissSla = activeHandoffId
      ? () => {
          setDismissedSlaHandoffIds((prev) => {
            const next = new Set(prev);
            next.add(activeHandoffId);
            return next;
          });
          if (slaBreachedMsg?.id) {
            setDismissedSystemMessageIds((prev) => {
              const next = new Set(prev);
              next.add(slaBreachedMsg.id);
              return next;
            });
          }
        }
      : undefined;

    return (
      <div className="flex flex-col min-h-0 w-full">
        {!suppressPill && (
          <HandoffStatusPill
            mode={mode}
            status={derivedHandoffStatus}
            trferName={trferName}
            expectedResponseAt={expectedResponseAt}
            onDismissSla={derivedHandoffStatus === 'sla_breached' ? dismissSla : undefined}
          />
        )}
        {/* Chat/voice parity (#1731, F-5): the thread renders on the core's
            prose surface; TRF-only turns come back through renderTrfTurn. */}
        <ChatSurfaceProvider config={surfaceConfig}>
          {/* #1803 (3): ONE scrollport. The rail scrolls; the list is told
              about it via `scrollContainerRef` and its own overflow is off.
              `overscroll-contain` stops the gesture chaining out to <main>. */}
          <div ref={chatRailRef} data-testid="chat-rail" className={railClass}>
            {!!activeHandoffId && displayMessages.some((m) => hasTranslationMetadata(m.metadata)) && (
              <ThreadTranslationBanner
                viewerLanguage={familyViewerLanguage}
                otherPartyLanguage={otherPartyLanguage}
                otherPartyName={otherPartyName}
                disclosureMode={translation?.disclosureMode ?? 'banner+chip'}
              />
            )}
            {error && (
              <ChatErrorRow error={error} onRetry={retryLastMessage} onDismiss={dismissError} />
            )}
            <CoreMessageList
              scrollContainerRef={chatRailRef}
              className="flex-none overflow-y-visible py-2"
              // #1731 (F-10): the live voice turns ride the same list.
              messages={[...messagesWithRecall, ...voicePip.voiceMessages]}
              surface="prose"
              coachName={personaName}
              isTyping={isDisplayTyping || showPendingBubble}
              renderTurn={renderTrfTurn({
                conversationId,
                companionName: personaName,
                handoffId: activeHandoffId,
                viewerLanguage: familyViewerLanguage,
              })}
              resetSignal={reanchorKey ?? 0}
              selectionMode={selectionMode}
              selectedFromIndex={rangeSelection.start?.index ?? null}
              selectedToIndex={rangeSelection.end?.index ?? null}
              onSelectMessage={handleSelectMessage}
              // #2265: no per-turn reply chips. The engine still parses the
              // `ui_chips` frame; the thread draws nothing from it — ideas
              // belong to the start of a conversation, under the composer.
            />
            {selectionMode && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-chat-border bg-chat-surface px-4 py-2 text-body-sm text-chat-text-secondary" role="status">
                <span>
                  {selectedCount === 0
                    ? t('page.selection.start')
                    : rangeSelection.end === null
                      ? t('page.selection.pickEnd')
                      : t('page.selection.selected', { count: selectedCount })}
                </span>
                {selectedCount > 0 && (
                  <button type="button" onClick={clearRangeSelection} className="shrink-0 bg-transparent text-chat-text-muted underline transition-colors hover:text-chat-text">
                    {t('page.selection.clear')}
                  </button>
                )}
              </div>
            )}
            {pendingAction && (
              <ActionConfirmCard
                action={pendingAction}
                onConfirm={confirmAction}
                onCancel={cancelAction}
                isLoading={isConfirming}
              />
            )}
          </div>
        </ChatSurfaceProvider>
      </div>
    );
  }

  // ── The composer, in every state the server enforces ───────────────────

  const historyToggle = (
    <HistoryToggle
      open={historyOpen}
      onToggle={() => setHistoryOpen((open) => !open)}
      buttonRef={historyButtonRef}
    />
  );

  // #2109 R3-B2: pt and fr have no companion prompt path; one line says the
  // reply comes back in English and points at the companion language setting.
  const companionNote = companionSpeaksUiLanguage(uiLanguage) ? null : (
    <p role="note" data-testid="companion-language-note" className="pt-1 text-center text-caption text-stevens-ink-3">
      <Trans
        t={t}
        i18nKey="page.companionLanguageNote"
        values={{ companion: personaName }}
        components={[<Link key="settings" to="/settings?tab=companion" className="underline" />]}
      />
    </p>
  );

  const autoFocus = variant === 'page' || wake.kind === 'compose';

  function renderComposer() {
    // A paused crisis conversation (#2566): the companion gave the resources
    // and paused here, so there is no composer — the server would refuse the
    // message (409). The family continues with one tap, or starts anew.
    if (crisisPaused) {
      return (
        <div className="space-y-2">
          <div className="flex justify-end">{historyToggle}</div>
          <div data-testid="crisis-paused-composer" className="flex flex-wrap items-center justify-center gap-2 py-3">
            <Button onClick={handleResumeAfterCrisis} disabled={crisisResuming}>
              {chatCopy.sessionEnded.resume}
            </Button>
            <Button variant="outline" onClick={handleNewConversation} disabled={crisisResuming}>
              {chatCopy.sessionEnded.startNew}
            </Button>
            {crisisResumeFailed && (
              <p role="alert" className="basis-full text-center text-caption text-status-danger-fg">
                {t('page.resumeFailed')}
              </p>
            )}
          </div>
        </div>
      );
    }

    // #1731 phase 6: an ended thread shows the banner in place of the
    // composer; resume the SAME thread or start a new one. A voice transcript
    // is view-only, so it gets no resume CTA.
    if (sessionEnded) {
      return (
        <div className="space-y-2">
          <div className="flex justify-end">{historyToggle}</div>
          <SessionEndedBanner
            onResume={handleResumeConversation}
            onStartNew={handleNewConversation}
            resuming={resuming}
            error={resumeError}
            resumable={!isVoiceThread}
          />
        </div>
      );
    }

    // While a TRFer is on the thread the family's typing goes TO the TRFer
    // (the backend's handoff short-circuit persists it and pushes it to
    // them); the composer is re-themed with a context line above it.
    const handoff = mode === 'human_handoff';
    // `ai_resumed` is `ai` for the loop-in affordance. Not gated on a case
    // manager (B-060), but gated first on the server-read flag (#1801).
    const showLoopTrfButton =
      handoffFamilyEntryEnabled
      && (mode === 'ai' || mode === 'ai_resumed') && displayMessages.length > 0;
    // #2265: Ideas for you at the START only — an empty thread nobody has
    // sent on yet (the draft and the memorial gate are the composer's and
    // the host's: ComposerHero hides them while typing, the home passes no
    // list in memorial, #644/#693).
    const ideasOn = !!renderIdeas && !handoff && threadEmpty && queued === null
      && !startedThreads.has(conversationId);

    return (
      <div data-testid="chat-composer" className={cn(handoff && 'rounded-lg bg-status-warn/30 p-2')}>
        {handoff && (
          <div className="mb-2 px-2 text-center text-caption text-status-warn-fg font-mono">
            {t('page.handoffComposer.banner', { companion: personaName })}
          </div>
        )}
        {!handoff && pendingNudge && nudgeCategory && (
          <div className="pb-3">
            <HandoffNudgeCard
              handoffId={pendingNudge._id}
              category={nudgeCategory}
              onResponded={() => {
                queryClient.invalidateQueries({ queryKey: ['handoffs', 'family-pending'] });
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
                refetchFamilyPending();
              }}
            />
          </div>
        )}
        {duplexNotice}
        <ComposerHero
          ariaLabel={ariaLabel}
          placeholder={handoff ? t('page.handoffComposer.placeholder') : placeholder}
          onSubmit={handleComposerSend}
          onVoice={startVoiceOrPrompt}
          sending={composerDisabled}
          trailing={historyToggle}
          renderIdeas={ideasOn ? (state) => renderIdeas({ ...state, send: handleComposerSend }) : undefined}
          inputProps={{
            attachments: 'receipt',
            isTyping: isDisplayTyping,
            initialValue: restoredDraft || undefined,
            autoFocus,
            // #1800: loop-in is an icon in the composer's toolbar, beside the clip.
            leadingActions: showLoopTrfButton ? (
              <LoopTrfButton
                conversationId={conversationId}
                onCreated={handleLoopTrfCreated}
                onError={handleLoopTrfError}
              />
            ) : undefined,
            pendingReceipt,
            onFileSelect: setReceiptFile,
            onRemoveReceipt: clearReceipt,
            voiceState: voicePip.pip.state,
            onVoiceStop: endVoice,
            isVoiceActive: voicePip.isLive,
          }}
        />
        <div className="text-center pt-1">
          <CrisisDisclosure variant="inline-link" />
        </div>
        {companionNote}
      </div>
    );
  }

  // ── Layout ─────────────────────────────────────────────────────────────

  const column = (
    <div data-testid="chat-panel" className="flex-1 min-w-0 flex flex-col gap-4">
      {bannerEscalation && (
        <EscalationBanner escalation={bannerEscalation} companionName={personaName} onDismiss={dismissEscalation} />
      )}
      {renderThread()}
      {renderComposer()}
    </div>
  );

  return (
    <>
      {/* #2267: no Card and no header of its own — on the home the page's
          PageHead is the H1 and the thread reads on the page ground, in the
          home's type scale and palette, on `/chat/:id` the same. */}
      <div className="flex items-start">
        {column}
        {/* #2266: the history, anchored RIGHT. Desktop: a column beside the
            thread, as tall as a reading screen, collapsed until asked for.
            Phone: the core's z-[60] overlay (§2.10) from the right edge.
            The height and the sticky pin apply only while the drawer is
            open. Collapsed, the aside is `w-0` but keeps its content height
            and its 1px edge, so the wrapper reserves no height at all: a
            fixed height there held the whole row open at min(70dvh, 40rem)
            under a short thread. */}
        <div
          data-testid="history-drawer-column"
          className={cn(
            !isMobile && historyOpen && 'sticky top-0 flex self-start ms-4 h-[min(70dvh,40rem)]',
            !isMobile && !historyOpen && 'h-0 overflow-hidden',
          )}
        >
          <ChatSurfaceProvider config={surfaceConfig}>
            <HamburgerDrawer
              side="right"
              railTrailingAction={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setHistoryOpen(false)}
                  aria-label={t('page.rail.hide')}
                  title={t('page.rail.hide')}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              }
              isExpanded={historyOpen}
              isMobile={isMobile}
              currentConversationId={conversationId}
              hasMessages={messages.length > 0}
              sessionEnded={sessionEnded}
              onSelectConversation={handleSwitchConversation}
              onNewConversation={handleNewConversation}
              onActiveConversationDeleted={handleNewConversation}
              onClose={() => setHistoryOpen(false)}
              onEnd={handleEndConversation}
              onCopy={handleCopy}
              onDownload={handleDownload}
              // Shell stacking contract (ui-ux-guideline §2.10): on a phone the
              // open drawer is a z-[60] overlay and the delete confirm a z-50
              // dialog, so the drawer closes first.
              onDelete={() => {
                if (isMobile) setHistoryOpen(false);
                handleDeleteClick(conversationId);
              }}
              copied={copied}
              copying={copying}
              exportingDocx={false}
              exportingPdf={false}
              messageTimestamps={messageTimestamps}
              onSlicePresetChange={setActiveSlicePreset}
              customSelectionCount={selectedCount}
            />
          </ChatSurfaceProvider>
        </div>
      </div>

      <AttachReceiptDialog
        file={receiptFile}
        onSubmit={attachReceipt}
        onClose={() => setReceiptFile(null)}
      />

      <VoiceCharacterSelector
        open={selectorOpen}
        mode={selectorMode}
        onSelect={() => {
          if (selectorMode === 'first-time') {
            startVoice({ sourceConversationId: conversationId ?? null });
          } else {
            toast({
              title: t('page.toasts.voiceUpdated'),
              description: t('page.toasts.voiceUpdatedBody'),
            });
          }
        }}
        onClose={() => {
          setSelectorOpen(false);
          if (selectorMode === 'first-time') {
            // Dismissed without selecting — start with default
            startVoice({ sourceConversationId: conversationId ?? null });
          }
        }}
      />

      {/* Delete conversation confirmation — editorial modal shell */}
      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmOpen(false);
            setDeletingId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md flex flex-col p-0 gap-0">
          <ModalHeader
            icon={Trash2}
            eyebrow={t('page.deleteDialog.eyebrow')}
            title={t('page.deleteDialog.title')}
            tone="rose"
          />

          <div className="px-6 pb-4">
            <p className="text-body text-stevens-ink-3 italic mb-4 font-serif">
              {t('page.deleteDialog.body')}
            </p>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                id="skip-delete-confirm"
                checked={skipDeleteConfirm}
                onCheckedChange={(checked) => {
                  const val = checked === true;
                  setSkipDeleteConfirm(val);
                  if (val) {
                    localStorage.setItem(DELETE_CONV_SKIP_CONFIRM_KEY, 'true');
                  } else {
                    localStorage.removeItem(DELETE_CONV_SKIP_CONFIRM_KEY);
                  }
                }}
              />
              <span className="text-body text-stevens-ink-3">{t('page.deleteDialog.dontShowAgain')}</span>
            </label>
          </div>

          <ModalFooter
            lead={
              <MonoLabel className="text-[11px] font-normal">
                {t('page.deleteDialog.permanent')}
              </MonoLabel>
            }
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeletingId(null);
              }}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              size="sm"
              className="bg-status-danger-solid hover:brightness-110 text-status-danger-solid-fg"
              onClick={confirmDelete}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              {t('page.deleteDialog.confirm')}
            </Button>
          </ModalFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
